import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import {
  fetchRunningServiceVersion,
  getEmbeddedClientVersion,
  reloadPage,
  VERSION_CHECK_INTERVAL_MS,
} from '@/lib/version-sync';

export function VersionRefreshBanner() {
  const clientVersion = useMemo(() => getEmbeddedClientVersion(), []);
  const [hasVersionMismatch, setHasVersionMismatch] = useState(false);

  useEffect(() => {
    if (!clientVersion || hasVersionMismatch) {
      return;
    }

    let cancelled = false;
    let isChecking = false;

    const checkVersion = async () => {
      if (cancelled || isChecking) {
        return;
      }

      isChecking = true;
      const serviceVersion = await fetchRunningServiceVersion();
      isChecking = false;

      if (cancelled || !serviceVersion) {
        return;
      }

      if (serviceVersion !== clientVersion) {
        setHasVersionMismatch(true);
      }
    };

    void checkVersion();

    const intervalId = window.setInterval(() => {
      void checkVersion();
    }, VERSION_CHECK_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [clientVersion, hasVersionMismatch]);

  if (!hasVersionMismatch) {
    return null;
  }

  return (
    <div className="pointer-events-none fixed inset-x-0 top-4 z-[120] flex justify-center px-4">
      <div
        role="alert"
        className="pointer-events-auto flex w-full max-w-2xl items-center gap-3 rounded-lg border border-neutral-200 bg-white px-4 py-3 text-neutral-900 shadow-elevated sm:px-5"
      >
        <AlertTriangle className="h-5 w-5 shrink-0 text-peach-700" aria-hidden="true" />
        <div className="flex flex-1 flex-col items-start justify-center gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-body font-medium text-neutral-900">
            Nava Backoffice has been updated. Refresh to use the latest version.
          </p>
          <button
            type="button"
            className="focus-ring inline-flex shrink-0 items-center gap-2 rounded-md border border-neutral-200 bg-backoffice-500 px-3 py-2 text-small font-semibold text-white transition hover:bg-backoffice-600"
            onClick={() => {
              reloadPage();
            }}
          >
            <RefreshCw className="h-4 w-4" aria-hidden="true" />
            <span>Refresh</span>
          </button>
        </div>
      </div>
    </div>
  );
}
